/**
 * What changed between two versions of a project, in words a person reads
 * (§5.25) — what a revision records, what the history page unfolds, and what a
 * member is shown before a pull is applied or a publish is sent.
 *
 * Paths name things rather than positions: `repos[api].startCmd`,
 * `workflows[Feature].steps`, `repos[api].api.requests[Login].headers`,
 * `config.vmMemoryMb`. A list of things (`shape.ts`) is compared item by item,
 * so adding a request is one line rather than a renumbering of every request
 * after it.
 *
 * A secret appears by its path and never by its value — the document holds no
 * value to show, and `secretEntries` describes a publish's writes without one.
 */
import type { OrgWorkflows, WorkspaceDocument } from './document';
import { describeSecretPath } from './secrets';
import { LISTS, indexed, isPlainObject, labels, sameValue } from './shape';

export type DiffKind = 'added' | 'removed' | 'changed';

export interface DiffEntry {
  path: string;
  kind: DiffKind;
  before?: unknown;
  after?: unknown;
}

export type RevisionDiff = DiffEntry[];

/** How much of a value a diff keeps. A Dockerfile or a request body is summarised past this. */
export const MAX_DIFF_VALUE_CHARS = 2000;

export function diffDocuments(before: WorkspaceDocument | null, after: WorkspaceDocument): RevisionDiff {
  if (!before) {
    return [
      {
        path: 'project',
        kind: 'added',
        after: { name: after.name, repos: after.repos.map((repo) => repo.name) }
      }
    ];
  }
  const out: RevisionDiff = [];
  walk('', '', before, after, out);
  return out;
}

/** The same walk over an organization's workflows — `workflows` is a list of things there too (`shape.ts`). */
export function diffOrgWorkflows(before: OrgWorkflows, after: OrgWorkflows): RevisionDiff {
  const out: RevisionDiff = [];
  walk('', '', before, after, out);
  return out;
}

/** A publish's secret writes, as diff lines: which secret, and whether it was set or taken away. */
export function secretEntries(document: WorkspaceDocument | null, writes: Record<string, string | null>): RevisionDiff {
  return Object.entries(writes).map(([path, value]) => ({
    path: `${describeSecretPath(document, path)} (secret)`,
    kind: value === null ? 'removed' : 'changed'
  }));
}

/** How many lines a diff has — the number the history page shows beside a version. */
export function countChanges(diff: RevisionDiff): number {
  return diff.length;
}

function walk(shapePath: string, path: string, a: unknown, b: unknown, out: RevisionDiff): void {
  if (sameValue(a, b)) return;
  const shape = LISTS[shapePath];
  if (shape && (Array.isArray(a) || a === undefined) && (Array.isArray(b) || b === undefined)) {
    const left = indexed(shape, a);
    const right = indexed(shape, b);
    const names = labels(shape, (a as unknown[]) ?? [], (b as unknown[]) ?? []);
    for (const [key, item] of right) {
      const at = `${path}[${names.get(key)}]`;
      const previous = left.get(key);
      if (!previous) out.push({ path: at, kind: 'added', after: clip(item) });
      else walk(shapePath, at, previous, item, out);
    }
    for (const [key, item] of left) {
      if (!right.has(key)) out.push({ path: `${path}[${names.get(key)}]`, kind: 'removed', before: clip(item) });
    }
    return;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    for (const key of new Set([...Object.keys(a), ...Object.keys(b)])) {
      walk(shapePath ? `${shapePath}.${key}` : key, path ? `${path}.${key}` : key, a[key], b[key], out);
    }
    return;
  }
  if (a === undefined) out.push({ path, kind: 'added', after: clip(b) });
  else if (b === undefined) out.push({ path, kind: 'removed', before: clip(a) });
  else out.push({ path, kind: 'changed', before: clip(a), after: clip(b) });
}

/** A value short enough to store beside the revision, or what it starts with. */
function clip(value: unknown): unknown {
  const text = JSON.stringify(value);
  if (text === undefined || text.length <= MAX_DIFF_VALUE_CHARS) return value;
  return { truncated: true, preview: text.slice(0, MAX_DIFF_VALUE_CHARS) };
}
